import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/*
  Hardanger Rock-logo som aldri vises som «ødelagt bilde».
  Hvis bildet feiler (typisk mens tjenesten starter opp eller ved nettbrudd) prøver vi på nytt
  med økende ventetid (0,6 s → 1,2 s → 2,4 s). Feiler alt, vises et tekstlig ordmerke i merkevarefargene.
*/

const SOURCES = {
  dark: '/brand/hardanger-rock-logo.png',
  white: '/brand/hardanger-rock-logo-hvitt.png',
  whiteOrange: '/brand/hardanger-rock-logo-hvitt-orange.png',
  mark: '/brand/cropped-hardanger-rock-site-icon-192x192.png',
};

const MAX_RETRIES = 3;

export function BrandLogo({ variant = 'dark', className, alt = 'Hardanger Rock', decorative = false, testId, ...rest }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const onError = () => {
    if (attempt >= MAX_RETRIES) {
      setFailed(true);
      return;
    }
    timer.current = setTimeout(() => setAttempt((a) => a + 1), 600 * 2 ** attempt);
  };

  const onLoad = () => {
    if (failed) setFailed(false);
  };

  if (failed) return <WordmarkFallback variant={variant} className={className} alt={alt} decorative={decorative} testId={testId} />;

  const base = SOURCES[variant] ?? SOURCES.dark;
  const src = attempt === 0 ? base : `${base}?r=${attempt}`;
  return (
    <img
      key={src}
      src={src}
      alt={decorative ? '' : alt}
      aria-hidden={decorative || undefined}
      className={className}
      draggable={false}
      onError={onError}
      onLoad={onLoad}
      data-testid={testId}
      data-logo-attempt={attempt || undefined}
      {...rest}
    />
  );
}

/** Tekstlig ordmerke i merkevarefargene – brukes kun når bildet ikke kan hentes. */
function WordmarkFallback({ variant, className, alt, decorative, testId }) {
  const onDark = variant === 'white' || variant === 'whiteOrange';
  if (variant === 'mark') {
    return (
      <span
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : alt}
        aria-hidden={decorative || undefined}
        className={cn('inline-flex items-center justify-center rounded-[6px] bg-[var(--hr-accent)] text-[11px] font-bold leading-none tracking-tight text-white', className)}
        data-testid={testId}
        data-logo-fallback="mark"
      >
        HR
      </span>
    );
  }
  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : alt}
      aria-hidden={decorative || undefined}
      className={cn('inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-semibold uppercase leading-none tracking-[0.08em]', onDark ? 'text-white' : 'text-[var(--hr-heading)]', className)}
      data-testid={testId}
      data-logo-fallback="wordmark"
    >
      <span>Hardanger</span>
      <span className={onDark && variant === 'white' ? 'text-white' : 'text-[var(--hr-accent)]'}>Rock</span>
    </span>
  );
}
